/**
 * useServerEvents
 *
 * Shared client hook for live server event subscriptions.
 *
 * Primary responsibilities:
 * - Keep one Supabase Realtime subscription for the whole app
 * - Replay recent `server_events` rows so early events are not lost
 * - Dedupe replayed and live rows before notifying listeners
 * - Reconnect when auth/session or browser lifecycle changes
 *
 * Dependencies:
 * - lib/events/types
 * - lib/realtime/channel-recovery
 * - lib/supabase/browser-client
 * - supabase Realtime
 */

'use client'

import { useCallback, useEffect, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'

import type { ServerEventType } from '@/lib/events/types'
import { removeRealtimeChannelOnce } from '@/lib/realtime/channel-recovery'
import { supabase } from '@/lib/supabase/browser-client'
import type { Tables } from '@/supabase/types'

type ServerEventRow = Pick<
  Tables<'server_events'>,
  'id' | 'type' | 'data' | 'user_id' | 'chat_id' | 'created_at'
>

interface ServerEvent<EventData = unknown> {
  id: string
  type: ServerEventType
  data: EventData
  timestamp: number
  createdAt: string
}

type EventListener<EventData = unknown> = (
  event: ServerEvent<EventData>
) => void

export interface UseServerEventsResult {
  isConnected: boolean
  lastEvent: ServerEvent | null
  addEventListener: <EventData = unknown>(
    eventType: ServerEventType,
    listener: EventListener<EventData>
  ) => void
  removeEventListener: <EventData = unknown>(
    eventType: ServerEventType,
    listener: EventListener<EventData>
  ) => void
  connect: () => void
  disconnect: () => void
}

const RECONNECT_DELAY_MS = 2_000
const REPLAY_BUFFER_MS = 5_000
const INITIAL_REPLAY_WINDOW_MS = 30_000
const MAX_REPLAY_EVENTS = 200
const MAX_SEEN_EVENTS_PER_LISTENER = 1_000
const MAX_RECENT_LIVE_EVENTS = 50
const RECENT_LIVE_EVENT_REPLAY_MS = 60_000

type ListenerDeliveryState = {
  deliveredCreatedAtByType: Map<ServerEventType, string>
  seenEventIds: Set<string>
}

let globalChannel: RealtimeChannel | null = null
let globalConnectionPromise: Promise<void> | null = null
let globalReplayPromise: Promise<void> | null = null
let globalReconnectTimeout: ReturnType<typeof setTimeout> | null = null
let globalSubscriptionRefreshTimeout: ReturnType<typeof setTimeout> | null = null
let globalAuthSubscription: { unsubscribe: () => void } | null = null
let globalUserId: string | null = null
let globalLastDeliveredCreatedAtByType = new Map<ServerEventType, string>()
let listenerDeliveryStates = new WeakMap<EventListener, ListenerDeliveryState>()
let globalListeners = new Map<ServerEventType, Set<EventListener>>()
let recentLiveEvents: ServerEvent[] = []
let connectionSubscribers = new Set<() => void>()
let authListenerSubscribers = 0
let browserLifecycleSubscribers = 0
let globalConnectionEpoch = 0
let globalSubscribedEventTypesKey: string | null = null
let globalReplayEventTypesKey: string | null = null
let globalConnectionState = {
  isConnected: false,
  lastEvent: null as ServerEvent | null
}

function notifySubscribers() {
  connectionSubscribers.forEach((callback) => callback())
}

function resetReplayState() {
  globalLastDeliveredCreatedAtByType.clear()
  listenerDeliveryStates = new WeakMap()
  recentLiveEvents = []
  globalConnectionState.lastEvent = null
}

function getActiveEventTypes() {
  return [...globalListeners.keys()].sort()
}

function getEventTypesKey(eventTypes = getActiveEventTypes()) {
  return eventTypes.join('|')
}

function getReplayFrom(eventTypes: ServerEventType[]) {
  const deliveredCreatedAt = eventTypes
    .map((eventType) => globalLastDeliveredCreatedAtByType.get(eventType))
    .filter((createdAt): createdAt is string => typeof createdAt === 'string')

  if (deliveredCreatedAt.length !== eventTypes.length) {
    return new Date(Date.now() - INITIAL_REPLAY_WINDOW_MS).toISOString()
  }

  const earliestCreatedAt = deliveredCreatedAt.reduce((earliest, createdAt) =>
    createdAt < earliest ? createdAt : earliest
  )

  return new Date(
    new Date(earliestCreatedAt).getTime() - REPLAY_BUFFER_MS
  ).toISOString()
}

function rememberGlobalEvent(event: ServerEvent) {
  const typeCreatedAt = globalLastDeliveredCreatedAtByType.get(event.type)
  if (!typeCreatedAt || event.createdAt > typeCreatedAt) {
    globalLastDeliveredCreatedAtByType.set(event.type, event.createdAt)
  }
}

// --------------

/**
 * Buffer a recently delivered live event so a listener that subscribes
 * moments later (e.g. a remounted LoadingMessage across the
 * firstTokenReceived boundary) can replay in-flight tool state instead
 * of missing the already-dispatched tool.start/tool.progress.
 */
function rememberRecentLiveEvent(event: ServerEvent) {
  recentLiveEvents.push(event)

  if (recentLiveEvents.length > MAX_RECENT_LIVE_EVENTS) {
    recentLiveEvents.shift()
  }
}

function getListenerDeliveryState(listener: EventListener) {
  const existingState = listenerDeliveryStates.get(listener)
  if (existingState) {
    return existingState
  }

  const nextState: ListenerDeliveryState = {
    deliveredCreatedAtByType: new Map(),
    seenEventIds: new Set()
  }
  listenerDeliveryStates.set(listener, nextState)

  return nextState
}

function rememberListenerEvent(
  state: ListenerDeliveryState,
  event: ServerEvent
) {
  state.seenEventIds.add(event.id)

  if (state.seenEventIds.size > MAX_SEEN_EVENTS_PER_LISTENER) {
    const oldestEventId = state.seenEventIds.values().next().value

    if (typeof oldestEventId === 'string') {
      state.seenEventIds.delete(oldestEventId)
    }
  }

  const typeCreatedAt = state.deliveredCreatedAtByType.get(event.type)
  if (!typeCreatedAt || event.createdAt > typeCreatedAt) {
    state.deliveredCreatedAtByType.set(event.type, event.createdAt)
  }
}

function shouldDeliverEventToListener(
  listener: EventListener,
  event: ServerEvent
) {
  const state = getListenerDeliveryState(listener)

  if (state.seenEventIds.has(event.id)) {
    return false
  }

  const typeCreatedAt = state.deliveredCreatedAtByType.get(event.type)
  if (typeCreatedAt && event.createdAt < typeCreatedAt) {
    return false
  }

  rememberListenerEvent(state, event)

  return true
}

function normalizeEventData(row: ServerEventRow) {
  const normalizedData =
    row.data && typeof row.data === 'object' && !Array.isArray(row.data)
      ? { ...(row.data as Record<string, unknown>) }
      : row.data

  if (
    normalizedData &&
    typeof normalizedData === 'object' &&
    !Array.isArray(normalizedData)
  ) {
    const record = normalizedData as Record<string, unknown>

    if (row.chat_id && typeof record.chatId !== 'string') {
      record.chatId = row.chat_id
    }

    if (row.chat_id && typeof record.chat_id !== 'string') {
      record.chat_id = row.chat_id
    }

    if (row.user_id && typeof record.userId !== 'string') {
      record.userId = row.user_id
    }

    if (row.user_id && typeof record.user_id !== 'string') {
      record.user_id = row.user_id
    }
  }

  return normalizedData
}

function toServerEvent(row: ServerEventRow): ServerEvent | null {
  if (
    typeof row.id !== 'string' ||
    typeof row.type !== 'string' ||
    typeof row.created_at !== 'string'
  ) {
    return null
  }

  return {
    id: row.id,
    type: row.type as ServerEventType,
    data: normalizeEventData(row),
    timestamp: new Date(row.created_at).getTime(),
    createdAt: row.created_at
  }
}

function dispatchServerEvent(event: ServerEvent) {
  const listeners = globalListeners.get(event.type)
  if (!listeners) {
    return
  }

  let didDeliver = false

  listeners.forEach((listener) => {
    if (!shouldDeliverEventToListener(listener, event)) {
      return
    }

    didDeliver = true

    try {
      listener(event)
    } catch (error) {
      console.error('Error processing server event listener:', error)
    }
  })

  if (!didDeliver) {
    return
  }

  rememberGlobalEvent(event)
  rememberRecentLiveEvent(event)
  globalConnectionState.lastEvent = event
  notifySubscribers()
}

async function getCurrentUserId(): Promise<string | null> {
  try {
    const {
      data: { user }
    } = await supabase.auth.getUser()

    return user?.id || null
  } catch (error) {
    console.error('Error getting current user for server events:', error)
    return null
  }
}

async function replayMissedEvents() {
  const replayEventTypes = getActiveEventTypes()
  if (replayEventTypes.length === 0) {
    return
  }

  const replayEventTypesKey = getEventTypesKey(replayEventTypes)

  if (
    globalReplayPromise &&
    globalReplayEventTypesKey === replayEventTypesKey
  ) {
    return globalReplayPromise
  }

  const replayEpoch = globalConnectionEpoch
  globalReplayEventTypesKey = replayEventTypesKey

  const replayPromise = (async () => {
    const replayRows: ServerEventRow[] = []

    for (const eventType of replayEventTypes) {
      const replayFrom = getReplayFrom([eventType])

      const { data, error } = await supabase
        .from('server_events')
        .select('id, type, data, user_id, chat_id, created_at')
        .gte('created_at', replayFrom)
        .eq('type', eventType)
        .order('created_at', { ascending: false })
        .limit(MAX_REPLAY_EVENTS)

      if (error) {
        throw error
      }

      replayRows.push(...((data || []) as ServerEventRow[]))
    }

    if (replayEpoch !== globalConnectionEpoch) {
      return
    }

    replayRows.sort((left, right) =>
      left.created_at.localeCompare(right.created_at)
    )

    for (const row of replayRows) {
      if (replayEpoch !== globalConnectionEpoch) {
        return
      }

      const event = toServerEvent(row)
      if (event) {
        dispatchServerEvent(event)
      }
    }
  })()
    .catch((error) => {
      console.error('Failed to replay recent server events:', error)
    })
    .finally(() => {
      if (globalReplayPromise === replayPromise) {
        globalReplayPromise = null
        globalReplayEventTypesKey = null
      }
    })

  globalReplayPromise = replayPromise

  return replayPromise
}

function scheduleReconnect() {
  if (globalReconnectTimeout) {
    clearTimeout(globalReconnectTimeout)
  }

  globalReconnectTimeout = setTimeout(() => {
    globalReconnectTimeout = null
    globalConnectionPromise = null
    void establishConnection()
  }, RECONNECT_DELAY_MS)
}

function clearSubscriptionRefreshTimeout() {
  if (!globalSubscriptionRefreshTimeout) {
    return
  }

  clearTimeout(globalSubscriptionRefreshTimeout)
  globalSubscriptionRefreshTimeout = null
}

async function resetConnection(options?: { resetReplay?: boolean }) {
  if (globalReconnectTimeout) {
    clearTimeout(globalReconnectTimeout)
    globalReconnectTimeout = null
  }

  globalConnectionEpoch += 1
  globalConnectionPromise = null
  globalReplayPromise = null
  globalReplayEventTypesKey = null
  globalSubscribedEventTypesKey = null

  const channel = globalChannel
  globalChannel = null

  if (channel) {
    await removeRealtimeChannelOnce(supabase, channel)
  }

  if (options?.resetReplay) {
    resetReplayState()
  }

  globalConnectionState.isConnected = false
  notifySubscribers()
}

async function refreshConnectionForActiveListeners() {
  if (globalListeners.size === 0) {
    await disconnectIfUnused()
    return
  }

  const eventTypesKey = getEventTypesKey()

  if (
    (globalChannel || globalConnectionPromise) &&
    globalSubscribedEventTypesKey !== eventTypesKey
  ) {
    await resetConnection()
  }

  await establishConnection()
}

function scheduleConnectionRefresh() {
  clearSubscriptionRefreshTimeout()

  globalSubscriptionRefreshTimeout = setTimeout(() => {
    globalSubscriptionRefreshTimeout = null
    void refreshConnectionForActiveListeners()
  }, 0)
}

async function reconnectForAuthenticatedUser(nextUserId: string | null) {
  if (
    nextUserId === globalUserId &&
    globalChannel &&
    globalChannel.state === 'joined'
  ) {
    return
  }

  globalUserId = nextUserId
  await resetConnection({ resetReplay: true })

  if (globalListeners.size > 0) {
    await establishConnection()
  }
}

function resumeLiveServerEvents() {
  if (globalListeners.size === 0) {
    return
  }

  if (
    typeof document !== 'undefined' &&
    document.visibilityState === 'hidden'
  ) {
    return
  }

  void refreshConnectionForActiveListeners()
}

function pauseLiveServerEvents() {
  if (globalListeners.size === 0) {
    return
  }

  void resetConnection()
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    resumeLiveServerEvents()
    return
  }

  if (document.visibilityState === 'hidden') {
    pauseLiveServerEvents()
  }
}

function attachBrowserLifecycleListeners() {
  if (typeof window === 'undefined') {
    return
  }

  browserLifecycleSubscribers += 1

  if (browserLifecycleSubscribers > 1) {
    return
  }

  window.addEventListener('online', resumeLiveServerEvents)
  window.addEventListener('focus', resumeLiveServerEvents)
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

function detachBrowserLifecycleListeners() {
  if (typeof window === 'undefined') {
    return
  }

  browserLifecycleSubscribers = Math.max(0, browserLifecycleSubscribers - 1)

  if (browserLifecycleSubscribers > 0) {
    return
  }

  window.removeEventListener('online', resumeLiveServerEvents)
  window.removeEventListener('focus', resumeLiveServerEvents)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}

function attachAuthListener() {
  authListenerSubscribers += 1

  if (globalAuthSubscription) {
    return
  }

  const {
    data: { subscription }
  } = supabase.auth.onAuthStateChange((_event, session) => {
    const nextUserId = session?.user?.id ?? null

    if (
      nextUserId === globalUserId &&
      (
        globalConnectionState.isConnected ||
        globalListeners.size === 0
      )
    ) {
      return
    }

    void reconnectForAuthenticatedUser(nextUserId)
  })

  globalAuthSubscription = subscription
}

function detachAuthListener() {
  authListenerSubscribers = Math.max(0, authListenerSubscribers - 1)

  if (authListenerSubscribers > 0 || !globalAuthSubscription) {
    return
  }

  globalAuthSubscription.unsubscribe()
  globalAuthSubscription = null
}

async function establishConnection() {
  if (globalConnectionPromise) {
    return globalConnectionPromise
  }

  if (globalListeners.size === 0) {
    return
  }

  const connectionEpoch = globalConnectionEpoch

  globalConnectionPromise = (async () => {
    const nextUserId = await getCurrentUserId()

    if (connectionEpoch !== globalConnectionEpoch) {
      return
    }

    const eventTypes = getActiveEventTypes()
    if (eventTypes.length === 0) {
      return
    }

    const eventTypesKey = getEventTypesKey(eventTypes)

    if (
      globalChannel &&
      globalUserId === nextUserId &&
      globalChannel.state === 'joined' &&
      globalSubscribedEventTypesKey === eventTypesKey
    ) {
      globalConnectionState.isConnected = true
      notifySubscribers()
      await replayMissedEvents()
      return
    }

    if (globalReconnectTimeout) {
      clearTimeout(globalReconnectTimeout)
      globalReconnectTimeout = null
    }

    if (globalChannel) {
      await removeRealtimeChannelOnce(supabase, globalChannel)
      globalChannel = null

      if (connectionEpoch !== globalConnectionEpoch) {
        return
      }
    }

    globalUserId = nextUserId
    globalSubscribedEventTypesKey = eventTypesKey

    const channel = supabase
      .channel('server-events', {
        config: {
          broadcast: { self: false },
          presence: { key: nextUserId || 'anonymous' }
        }
      })

    eventTypes.forEach((eventType) => {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'server_events',
          filter: `type=eq.${eventType}`
        },
        (payload) => {
          const event = toServerEvent(payload.new as ServerEventRow)
          if (event) {
            dispatchServerEvent(event)
          }
        }
      )
    })

    if (connectionEpoch !== globalConnectionEpoch) {
      await removeRealtimeChannelOnce(supabase, channel)
      return
    }

    globalChannel = channel

    await new Promise<void>((resolve) => {
      let didResolve = false

      const resolveOnce = () => {
        if (didResolve) {
          return
        }

        didResolve = true
        resolve()
      }

      channel.subscribe((status) => {
        if (connectionEpoch !== globalConnectionEpoch) {
          resolveOnce()
          return
        }

        if (status === 'SUBSCRIBED') {
          globalConnectionState.isConnected = true
          notifySubscribers()
          void replayMissedEvents()
          resolveOnce()
          return
        }

        if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          globalConnectionState.isConnected = false
          notifySubscribers()
          globalConnectionPromise = null

          if (globalListeners.size > 0) {
            scheduleReconnect()
          }

          resolveOnce()
        }
      })
    })
  })()
    .catch((error) => {
      console.error('Error establishing server events connection:', error)

      if (connectionEpoch === globalConnectionEpoch) {
        globalConnectionState.isConnected = false
        notifySubscribers()
        globalConnectionPromise = null

        if (globalListeners.size > 0) {
          scheduleReconnect()
        }
      }
    })
    .finally(() => {
      if (
        connectionEpoch === globalConnectionEpoch &&
        globalConnectionState.isConnected
      ) {
        globalConnectionPromise = null
      }
    })

  return globalConnectionPromise
}

async function disconnectIfUnused() {
  if (globalListeners.size > 0) {
    return
  }

  clearSubscriptionRefreshTimeout()
  await resetConnection({ resetReplay: true })
}

// --------------

/**
 * Inject a tool event directly into the server events listener system
 * without going through Supabase Realtime. Used by the stream parser
 * to deliver inline tool markers with zero latency.
 */
export function dispatchInlineToolEvent(eventType: ServerEventType, data: unknown) {
  const event: ServerEvent = {
    id: `inline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: eventType,
    data: data as never,
    timestamp: Date.now(),
    createdAt: new Date().toISOString()
  }
  dispatchServerEvent(event)
}

// --------------

export function useServerEvents(): UseServerEventsResult {
  const [isConnected, setIsConnected] = useState(
    globalConnectionState.isConnected
  )
  const [lastEvent, setLastEvent] = useState<ServerEvent | null>(
    globalConnectionState.lastEvent
  )

  useEffect(() => {
    const updateState = () => {
      setIsConnected(globalConnectionState.isConnected)
      setLastEvent(globalConnectionState.lastEvent)
    }

    connectionSubscribers.add(updateState)
    updateState()

    return () => {
      connectionSubscribers.delete(updateState)
    }
  }, [])

  useEffect(() => {
    attachAuthListener()
    attachBrowserLifecycleListeners()

    return () => {
      detachAuthListener()
      detachBrowserLifecycleListeners()
    }
  }, [])

  const connect = useCallback(() => {
    scheduleConnectionRefresh()
  }, [])

  const disconnect = useCallback(() => {
    void disconnectIfUnused()
  }, [])

  const addEventListener = useCallback(
    <EventData = unknown>(
      eventType: ServerEventType,
      listener: EventListener<EventData>
    ) => {
      if (!globalListeners.has(eventType)) {
        globalListeners.set(eventType, new Set())
      }

      globalListeners.get(eventType)?.add(listener as EventListener)

      // Replay recent in-flight events of this type to the new listener
      // so a late-mounting subscriber (e.g. a remounted LoadingMessage)
      // reconstructs tool state instead of missing tool.start/progress.
      const replayFrom = Date.now() - RECENT_LIVE_EVENT_REPLAY_MS

      recentLiveEvents.forEach((event) => {
        if (event.type !== eventType || event.timestamp < replayFrom) {
          return
        }

        if (!shouldDeliverEventToListener(listener as EventListener, event)) {
          return
        }

        try {
          ;(listener as EventListener)(event)
        } catch (error) {
          console.error('Error replaying server event listener:', error)
        }
      })

      scheduleConnectionRefresh()
    },
    []
  )

  const removeEventListener = useCallback(
    <EventData = unknown>(
      eventType: ServerEventType,
      listener: EventListener<EventData>
    ) => {
      const listeners = globalListeners.get(eventType)
      if (listeners) {
        listeners.delete(listener as EventListener)

        if (listeners.size === 0) {
          globalListeners.delete(eventType)
        }
      }

      if (globalListeners.size === 0) {
        void disconnectIfUnused()
      } else {
        scheduleConnectionRefresh()
      }
    },
    []
  )

  return {
    isConnected,
    lastEvent,
    addEventListener,
    removeEventListener,
    connect,
    disconnect
  }
}
